import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Calendar, MapPin, Plus, Trash2, Users } from 'lucide-react';
import { findResidenceOverlaps, type Person, type PersonRelation, type Residence, type ResidenceInput } from '@tally/shared';
import { peopleApi } from '@/services/api';
import { SmartImport } from './SmartImport';
import { cn } from '@/utils/cn';

const INPUT = 'rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

const RELATIONS: Array<{ value: Exclude<PersonRelation, 'self'>; label: string }> = [
  { value: 'partner', label: 'Partner' },
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'family', label: 'Family' },
  { value: 'friend', label: 'Friend' },
  { value: 'colleague', label: 'Colleague' },
  { value: 'other', label: 'Other' },
];

export function PeopleSection() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['people'], queryFn: () => peopleApi.list() });
  const people = data?.data ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['people'] });
  const [importMessage, setImportMessage] = useState<string | null>(null);

  return (
    <section className="rounded-xl border border-border bg-card p-6">
      <div className="mb-2 flex items-center gap-3">
        <Users className="h-5 w-5 text-muted-foreground" />
        <h2 className="font-semibold">People & Home History</h2>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Where each person lived and when. Spending within about 75 km of someone's home at the time is a regular
        expense; anything farther away can be a trip. Moves that overlap by a few days are fine.
      </p>

      <SmartImport onImported={(message) => { setImportMessage(message); refresh(); }} />
      {importMessage && <p className="mb-4 text-sm text-green-700 dark:text-green-400">{importMessage}</p>}

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">Could not load people: {(error as Error).message}</p>}

      <div className="space-y-4">
        {people.map((person) => (
          <PersonCard key={person.id} person={person} onChange={refresh} />
        ))}
      </div>

      <AddPersonForm onAdded={refresh} />
      <p className="mt-3 text-xs text-muted-foreground">
        Locations are matched with{' '}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="underline">
          © OpenStreetMap contributors
        </a>
        .
      </p>
    </section>
  );
}

function PersonCard({ person, onChange }: { person: Person; onChange: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const saveResidences = useMutation({
    mutationFn: (residences: ResidenceInput[]) => peopleApi.replaceResidences(person.id, residences),
    onSuccess: () => { setError(null); onChange(); },
    onError: (err: Error) => setError(err.message),
  });
  const remove = useMutation({
    mutationFn: () => peopleApi.remove(person.id),
    onSuccess: onChange,
    onError: (err: Error) => setError(err.message),
  });

  const toInput = (r: Residence): ResidenceInput => ({
    startDate: r.startDate, endDate: r.endDate, unit: r.unit, streetNumber: r.streetNumber, street: r.street,
    city: r.city, region: r.region, postalCode: r.postalCode, country: r.country,
  });
  const overlaps = findResidenceOverlaps(person.residences);

  return (
    <div className="rounded-lg border border-border p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-xs font-medium">
            {person.name.charAt(0)}
          </div>
          <div>
            <span className="text-sm font-medium">{person.name}</span>
            <span className="ml-2 rounded-full bg-accent px-2 py-0.5 text-xs">{person.isSelf ? 'you' : person.relation}</span>
          </div>
        </div>
        {!person.isSelf && (
          <button
            onClick={() => { if (confirm(`Remove ${person.name} and their home history?`)) remove.mutate(); }}
            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            aria-label={`Remove ${person.name}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {person.residences.length === 0 && (
        <p className="mb-2 text-sm italic text-muted-foreground">No home history yet.</p>
      )}

      <div className="space-y-1.5">
        {person.residences.map((r) => (
          <div key={r.id} className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
            <div className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
              <div>
                <p className="text-sm">{[r.city, r.region, r.country].filter(Boolean).join(', ')}</p>
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" /> {r.startDate} — {r.endDate ?? 'present'}
                </p>
              </div>
            </div>
            <button
              onClick={() => saveResidences.mutate(person.residences.filter((x) => x.id !== r.id).map(toInput))}
              className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              aria-label="Remove address"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      {overlaps.length > 0 && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5" />
          {overlaps.map(([a, b]) => `${a.city} and ${b.city}`).join('; ')} overlap by more than a month. Is that right?
        </p>
      )}

      <AddResidenceForm
        onAdd={(residence) => saveResidences.mutate([...person.residences.map(toInput), residence])}
        isSaving={saveResidences.isPending}
      />
      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
    </div>
  );
}

function AddResidenceForm({ onAdd, isSaving }: { onAdd: (r: ResidenceInput) => void; isSaving: boolean }) {
  const [form, setForm] = useState({ startDate: '', endDate: '', city: '', region: '', country: '' });
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: e.target.value });
  const valid = form.startDate && form.city.trim() && form.country.trim() && (!form.endDate || form.endDate >= form.startDate);

  const submit = () => {
    if (!valid) return;
    onAdd({
      startDate: form.startDate, endDate: form.endDate || null, unit: null, streetNumber: null, street: null,
      city: form.city.trim(), region: form.region.trim() || null, postalCode: null, country: form.country.trim(),
    });
    setForm({ startDate: '', endDate: '', city: '', region: '', country: '' });
  };

  return (
    <div className="mt-3 flex flex-wrap items-end gap-2">
      <label className="text-xs text-muted-foreground">From<input type="date" value={form.startDate} onChange={set('startDate')} className={cn(INPUT, 'mt-1 block w-[150px]')} /></label>
      <label className="text-xs text-muted-foreground">To (blank = present)<input type="date" value={form.endDate} onChange={set('endDate')} className={cn(INPUT, 'mt-1 block w-[150px]')} /></label>
      <input placeholder="City" value={form.city} onChange={set('city')} className={cn(INPUT, 'min-w-[110px] flex-1')} />
      <input placeholder="Province/State" value={form.region} onChange={set('region')} className={cn(INPUT, 'w-[120px]')} />
      <input placeholder="Country" value={form.country} onChange={set('country')} className={cn(INPUT, 'w-[110px]')} />
      <button
        onClick={submit}
        disabled={!valid || isSaving}
        className="flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-sm font-medium hover:bg-accent/80 disabled:opacity-40"
      >
        <Plus className="h-3.5 w-3.5" /> Add address
      </button>
    </div>
  );
}

function AddPersonForm({ onAdded }: { onAdded: () => void }) {
  const [name, setName] = useState('');
  const [relation, setRelation] = useState<Exclude<PersonRelation, 'self'>>('partner');
  const [ageGroup, setAgeGroup] = useState<'adult' | 'child' | 'infant'>('adult');
  const add = useMutation({
    mutationFn: () => peopleApi.create({ name: name.trim(), relation, ageGroup, aliases: [] }),
    onSuccess: () => { setName(''); onAdded(); },
  });

  return (
    <div className="mt-4 rounded-lg border border-dashed border-border p-4">
      <p className="mb-3 text-xs font-medium text-muted-foreground">Add a person you travel with</p>
      <div className="flex flex-wrap gap-2">
        <input
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && name.trim() && add.mutate()}
          className={cn(INPUT, 'min-w-[140px] flex-1')}
        />
        <select value={relation} onChange={(e) => setRelation(e.target.value as typeof relation)} className={INPUT}>
          {RELATIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select value={ageGroup} onChange={(e) => setAgeGroup(e.target.value as typeof ageGroup)} className={INPUT}>
          <option value="adult">Adult</option>
          <option value="child">Child</option>
          <option value="infant">Infant</option>
        </select>
        <button
          onClick={() => add.mutate()}
          disabled={!name.trim() || add.isPending}
          className="flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-sm font-medium hover:bg-accent/80 disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>
      {add.error && <p className="mt-2 text-sm text-destructive">{(add.error as Error).message}</p>}
    </div>
  );
}
